import { useRef, useCallback } from 'react'
import { useThree } from '@react-three/fiber'
import { Vector3, Vector2, Raycaster, Plane, Camera } from 'three'
import { useDesignStore } from '../store/designStore'

export interface DragState {
  isDragging: boolean
  draggedObjectId: string | null
  dragPlane: Plane
  dragOffset: Vector3
  lastUpdate?: number
}

export const useDragControls = () => {
  const { camera, gl, scene } = useThree()
  const { selectedObjects, updateObjectProperties, selectObject, canvasSettings } = useDesignStore()
  
  const dragState = useRef<DragState>({
    isDragging: false,
    draggedObjectId: null,
    dragPlane: new Plane(new Vector3(0, 1, 0), 0), // XZ plane (ground level)
    dragOffset: new Vector3()
  })
  
  const raycaster = useRef(new Raycaster())
  const mouse = useRef(new Vector2())

  const updateMousePosition = useCallback((event: MouseEvent | TouchEvent) => {
    const rect = gl.domElement.getBoundingClientRect()
    let clientX: number, clientY: number

    if (event instanceof TouchEvent) {
      clientX = event.touches[0].clientX
      clientY = event.touches[0].clientY
    } else {
      clientX = event.clientX
      clientY = event.clientY
    }

    mouse.current.x = ((clientX - rect.left) / rect.width) * 2 - 1
    mouse.current.y = -((clientY - rect.top) / rect.height) * 2 + 1
  }, [gl])

  const getIntersectionPoint = useCallback((camera: Camera, mouse: Vector2, plane: Plane): Vector3 | null => {
    raycaster.current.setFromCamera(mouse, camera)
    const intersection = new Vector3()
    
    if (raycaster.current.ray.intersectPlane(plane, intersection)) {
      return intersection
    }
    
    // Fallback: project mouse position to ground level using camera direction
    const cameraDirection = new Vector3()
    camera.getWorldDirection(cameraDirection)
    
    // Calculate intersection with ground plane (y = 0)
    const cameraPosition = camera.position.clone()
    if (Math.abs(cameraDirection.y) > 0.001) { // Avoid division by zero
      const t = -cameraPosition.y / cameraDirection.y
      if (t > 0) {
        const fallbackIntersection = cameraPosition.clone().add(cameraDirection.multiplyScalar(t))
        return fallbackIntersection
      }
    }
    
    return null
  }, [])

  const handlePointerDown = useCallback((event: MouseEvent | TouchEvent, objectId: string) => {
    event.stopPropagation()
    event.preventDefault()
    
    // Get all objects to find the one being dragged
    const { aduModels, poolModels } = useDesignStore.getState()
    const allObjects = [...aduModels, ...poolModels]
    const targetObject = allObjects.find(obj => obj.id === objectId)
    
    if (!targetObject) {
      console.warn('Object not found for dragging:', objectId)
      return
    }
    
    // Disable orbit controls IMMEDIATELY before anything else
    const canvas = gl.domElement
    const orbitControls = (canvas as any)?.__orbitControls || 
                         (canvas.parentElement as any)?.__orbitControls ||
                         scene.userData?.orbitControls ||
                         (window as any).__orbitControls
    
    if (orbitControls) {
      orbitControls.enabled = false
      console.log('✅ Orbit controls disabled for drag')
    } else {
      console.warn('❌ Could not find orbit controls to disable')
    }
    
    // Select the object
    selectObject(objectId)
    
    // Start dragging
    updateMousePosition(event)
    const intersectionPoint = getIntersectionPoint(camera, mouse.current, dragState.current.dragPlane)
    
    if (intersectionPoint) {
      const objectPosition = new Vector3(...targetObject.position)
      dragState.current.dragOffset.subVectors(intersectionPoint, objectPosition)
      dragState.current.isDragging = true
      dragState.current.draggedObjectId = objectId
      
      console.log('🎯 Drag started:', {
        objectId,
        objectPosition: targetObject.position,
        intersectionPoint: intersectionPoint.toArray(),
        dragOffset: dragState.current.dragOffset.toArray()
      })
    }
  }, [camera, selectObject, updateMousePosition, getIntersectionPoint, gl, scene])

  const handlePointerMove = useCallback((event: MouseEvent | TouchEvent) => {
    if (!dragState.current.isDragging || !dragState.current.draggedObjectId) return
    
    event.preventDefault()
    
    // Throttle updates for better performance
    const now = Date.now()
    if (now - (dragState.current.lastUpdate || 0) < 16) return // ~60fps
    dragState.current.lastUpdate = now
    
    updateMousePosition(event)
    
    const intersectionPoint = getIntersectionPoint(camera, mouse.current, dragState.current.dragPlane)
    if (intersectionPoint) {
      const newPosition = intersectionPoint.clone().sub(dragState.current.dragOffset)
      
      // Constrain to canvas boundaries with some padding
      const maxX = (canvasSettings.width / 2) - 2 // 2 unit padding from edge
      const maxZ = (canvasSettings.height / 2) - 2 // 2 unit padding from edge
      
      newPosition.x = Math.max(-maxX, Math.min(maxX, newPosition.x))
      newPosition.z = Math.max(-maxZ, Math.min(maxZ, newPosition.z))
      newPosition.y = Math.max(0, newPosition.y) // Don't go below ground
      
      // Round to avoid micro-movements
      newPosition.x = Math.round(newPosition.x * 10) / 10
      newPosition.y = Math.round(newPosition.y * 10) / 10
      newPosition.z = Math.round(newPosition.z * 10) / 10
      
      console.log('Drag move:', {
        mousePos: [mouse.current.x, mouse.current.y],
        intersectionPoint: intersectionPoint.toArray(),
        newPosition: newPosition.toArray(),
        objectId: dragState.current.draggedObjectId
      })
      
      updateObjectProperties(dragState.current.draggedObjectId, {
        position: [newPosition.x, newPosition.y, newPosition.z]
      })
    } else {
      console.warn('No intersection point found during drag')
    }
  }, [camera, updateMousePosition, getIntersectionPoint, updateObjectProperties])

  const handlePointerUp = useCallback(() => {
    if (dragState.current.isDragging) {
      console.log('🏁 Drag ended')
      dragState.current.isDragging = false
      dragState.current.draggedObjectId = null
      
      // Re-enable orbit controls with multiple fallbacks
      const canvas = gl.domElement
      const orbitControls = (canvas as any)?.__orbitControls || 
                           (canvas.parentElement as any)?.__orbitControls ||
                           scene.userData?.orbitControls ||
                           (window as any).__orbitControls
      
      if (orbitControls) {
        orbitControls.enabled = true
        console.log('✅ Orbit controls re-enabled after drag')
      } else {
        console.warn('❌ Could not find orbit controls to re-enable')
      }
    }
  }, [gl, scene])

  // Attach global event listeners
  const attachEventListeners = useCallback(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (dragState.current.isDragging) {
        e.stopPropagation()
        e.preventDefault()
      }
      handlePointerMove(e)
    }
    
    const handleMouseUp = (e: MouseEvent) => {
      if (dragState.current.isDragging) {
        e.stopPropagation()
        e.preventDefault()
      }
      handlePointerUp()
    }
    
    const handleTouchMove = (e: TouchEvent) => {
      if (dragState.current.isDragging) {
        e.stopPropagation()
        e.preventDefault()
      }
      handlePointerMove(e)
    }
    
    const handleTouchEnd = (e: TouchEvent) => {
      if (dragState.current.isDragging) {
        e.stopPropagation()
        e.preventDefault()
      }
      handlePointerUp()
    }

    // Use passive: false to allow preventDefault
    document.addEventListener('mousemove', handleMouseMove, { passive: false, capture: true })
    document.addEventListener('mouseup', handleMouseUp, { passive: false, capture: true })
    document.addEventListener('touchmove', handleTouchMove, { passive: false, capture: true })
    document.addEventListener('touchend', handleTouchEnd, { passive: false, capture: true })

    return () => {
      document.removeEventListener('mousemove', handleMouseMove, { capture: true })
      document.removeEventListener('mouseup', handleMouseUp, { capture: true })
      document.removeEventListener('touchmove', handleTouchMove, { capture: true })
      document.removeEventListener('touchend', handleTouchEnd, { capture: true })
    }
  }, [handlePointerMove, handlePointerUp])

  return {
    handlePointerDown,
    attachEventListeners,
    isDragging: dragState.current.isDragging,
    draggedObjectId: dragState.current.draggedObjectId
  }
}
